import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import { Package, Search } from 'lucide-react';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import Loader from '../../../shared/components/ui/Loader';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import {
  useSmartBack,
  shouldShowModuleHomeBack,
  navigateWithReturn,
  buildReturnToFromLocation
} from '../../../shared/navigation';
import {
  getAllInventoryByProjectId,
  getAllRFQs,
  getOrders,
  getRequirements
} from '../../services/api/implementation/impl';
import {
  INVENTORY_PIPELINE_COLUMNS,
  buildInventoryPipeline
} from '../../utils/inventoryPipeline';

const statusTone = (status) => {
  const value = String(status || '').toLowerCase();
  if (value.includes('critical') || value.includes('expired')) return 'bg-red-100 text-red-700';
  if (value.includes('received') || value.includes('completed') || value.includes('done')) {
    return 'bg-emerald-100 text-emerald-800';
  }
  if (value.includes('pending') || value.includes('quoted') || value.includes('confirm')) {
    return 'bg-amber-100 text-amber-800';
  }
  return 'bg-gray-100 text-gray-700';
};

const PipelineCard = ({ card, onOpen, showReceiveCta }) => (
  <button
    type="button"
    onClick={() => onOpen(card)}
    className="w-full rounded-xl border border-gray-200 bg-white p-3 text-left shadow-sm transition hover:border-[#2A565E]/40 hover:shadow">
    <div className="flex items-start justify-between gap-2">
      <p className="text-sm font-semibold text-gray-900 leading-snug">{card.title}</p>
      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusTone(card.status)}`}>
        {card.status}
      </span>
    </div>
    <p className="mt-1 text-xs text-gray-500">{card.subtitle}</p>
    {card.meta ? <p className="mt-1 text-xs text-gray-600">{card.meta}</p> : null}
    {showReceiveCta ? (
      <span className="mt-2 inline-flex rounded-md bg-[#E9F5F6] px-2 py-1 text-[11px] font-semibold text-[#2A565E]">
        Receive material →
      </span>
    ) : null}
  </button>
);

const InventoryPipeline = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { collapsed } = useSidebar();
  const userId = Cookies.get('userId');
  const { onBack } = useSmartBack({ fallback: '/builder/inventory' });
  const showHomeBack = shouldShowModuleHomeBack('inventory', location);
  const { projects, projectsForDropdown } = useProjectsList();
  const [searchQuery, setSearchQuery] = useState('');

  const initialProjectId = Number(
    location.state?.projectID || localStorage.getItem('inventory_pipeline_project') || projects?.[0]?.id || 0
  );
  const [projectId, setProjectId] = useState(initialProjectId || '');

  React.useEffect(() => {
    if (!projectId && projects?.[0]?.id) {
      setProjectId(projects[0].id);
    }
  }, [projectId, projects]);

  React.useEffect(() => {
    if (projectId) {
      localStorage.setItem('inventory_pipeline_project', String(projectId));
    }
  }, [projectId]);

  React.useEffect(() => {
    mixpanel.track('Inventory Pipeline Viewed', {
      user_id: userId,
      project_id: projectId || null
    });
  }, [userId, projectId]);

  const enabled = Boolean(projectId);

  const { data: requirementsRes, isFetching: loadingRequirements } = useQuery({
    queryKey: ['inventory-pipeline-requirements'],
    queryFn: () => getRequirements(1, 500, 'requirements'),
    enabled
  });

  const { data: emergencyRes, isFetching: loadingEmergency } = useQuery({
    queryKey: ['inventory-pipeline-emergency'],
    queryFn: () => getRequirements(1, 500, 'emergency'),
    enabled
  });

  const { data: rfqRes, isFetching: loadingRfqs } = useQuery({
    queryKey: ['inventory-pipeline-rfqs', projectId],
    queryFn: () => getAllRFQs(1, 500, projectId),
    enabled
  });

  const { data: ordersRes, isFetching: loadingOrders } = useQuery({
    queryKey: ['inventory-pipeline-orders', projectId],
    queryFn: () => getOrders(1, 500, projectId),
    enabled
  });

  const { data: stockRes, isFetching: loadingStock } = useQuery({
    queryKey: ['inventory-pipeline-stock', projectId],
    queryFn: () => getAllInventoryByProjectId(projectId, null, '', '90'),
    enabled
  });

  const pipeline = useMemo(() => {
    const requirements = [...(requirementsRes?.data || []), ...(emergencyRes?.data || [])].filter(
      (row) => Number(row?.project?.id || row?.project_id) === Number(projectId)
    );
    return buildInventoryPipeline({
      requirements,
      rfqs: rfqRes?.data || [],
      orders: ordersRes?.data || [],
      stock: Array.isArray(stockRes) ? stockRes : stockRes?.data || []
    });
  }, [emergencyRes, ordersRes, projectId, requirementsRes, rfqRes, stockRes]);

  const matchesSearch = (card) => {
    if (!searchQuery.trim()) return true;
    const haystack = `${card.title} ${card.subtitle} ${card.meta} ${card.status}`.toLowerCase();
    return haystack.includes(searchQuery.trim().toLowerCase());
  };

  const columns = INVENTORY_PIPELINE_COLUMNS.map((column) => ({
    ...column,
    cards: (pipeline[column.id] || []).filter(matchesSearch)
  }));

  const openCard = (card) => {
    mixpanel.track('Inventory Pipeline Card Opened', {
      user_id: userId,
      kind: card.kind,
      entity_id: card.entityId
    });
    navigateWithReturn(navigate, card.href, {
      returnTo: buildReturnToFromLocation(location),
      state: { projectID: Number(projectId) }
    });
  };

  const isLoading =
    loadingRequirements || loadingEmergency || loadingRfqs || loadingOrders || loadingStock;

  return (
    <Layout>
      <Loader isVisible={isLoading} />
      <div
        className={`fixed top-0 z-10 bg-white ${
          collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'
        }`}>
        <Header
          data={{ name: 'Inventory Pipeline' }}
          backArrow={showHomeBack}
          onBack={showHomeBack ? onBack : undefined}
        />
      </div>

      <div className="pt-[5.5rem] px-6 pb-8">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-[#1E4A53]">Req → RFQ → PO → Received</h2>
            <p className="text-sm text-gray-500">
              One board for the live buy flow. Existing list pages stay as they are.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={projectId || ''}
              onChange={(event) => setProjectId(event.target.value)}
              className="h-10 min-w-[12rem] rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800">
              {(projectsForDropdown || []).map((project) => (
                <option key={project.id} value={project.id}>
                  {project.label || project.name}
                </option>
              ))}
            </select>
            <label className="flex h-10 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3">
              <Search size={16} className="text-gray-400" />
              <input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search material, vendor, PO"
                className="w-48 bg-transparent text-sm outline-none"
              />
            </label>
            <button
              type="button"
              onClick={() =>
                navigate('/builder/inventory/site-receipts', {
                  state: { projectID: Number(projectId) }
                })
              }
              className="h-10 rounded-lg border border-[#2A565E] px-4 text-sm font-semibold text-[#2A565E]">
              Site receipts
            </button>
          </div>
        </div>

        {pipeline.lowStock.length > 0 && (
          <div className="mb-5 rounded-2xl border border-red-100 bg-red-50/70 p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package size={18} className="text-red-600" />
                <h3 className="text-sm font-semibold text-red-800">Low / critical stock</h3>
              </div>
              <button
                type="button"
                className="text-xs font-semibold text-[#2A565E] hover:underline"
                onClick={() => navigate('/builder/inventory/stock', { state: { projectID: Number(projectId) } })}>
                Open stock
              </button>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-1">
              {pipeline.lowStock.map((card) => (
                <button
                  key={card.id}
                  type="button"
                  onClick={() => openCard(card)}
                  className="min-w-[14rem] rounded-xl border border-red-100 bg-white px-3 py-2 text-left">
                  <p className="text-sm font-medium text-gray-900">{card.title}</p>
                  <p className="text-xs text-gray-500">{card.meta}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {columns.map((column) => (
            <section key={column.id} className="flex min-h-[28rem] flex-col rounded-2xl bg-[#F4F7F8] p-3">
              <div className="mb-3 flex items-start justify-between px-1">
                <div>
                  <h3 className="text-sm font-semibold text-[#1E4A53]">{column.title}</h3>
                  <p className="text-xs text-gray-500">{column.hint}</p>
                </div>
                <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-[#2A565E]">
                  {column.cards.length}
                </span>
              </div>
              <div className="flex flex-1 flex-col gap-3 overflow-y-auto pr-1">
                {column.cards.length === 0 ? (
                  <div className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-white/60 p-4 text-center text-xs text-gray-400">
                    Nothing in this stage
                  </div>
                ) : (
                  column.cards.map((card) => (
                    <PipelineCard
                      key={card.id}
                      card={card}
                      onOpen={openCard}
                      showReceiveCta={column.id === 'ordered'}
                    />
                  ))
                )}
              </div>
            </section>
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default InventoryPipeline;
